import { useMemo } from "react";
import { outstandingNotes, queueSummary } from "../lib/scheduler";
import { useStore } from "../lib/store";
import { LANG_NAMES } from "../lib/types";

interface Props {
  onReview: (sourceId: string | null, domain: string | null) => void;
  onGoToInbox: () => void;
}

export default function Home({ onReview, onGoToInbox }: Props) {
  const { cards, notes, sources, lang, profile, counts } = useStore();

  const summary = useMemo(() => queueSummary(cards, notes, lang), [cards, notes, lang]);

  const newRemaining = Math.max(0, profile.newPerDay - counts.newIntroduced);
  const availableNew = Math.min(summary.fresh, newRemaining);

  // Sources are tags, not decks: this list is for cramming on listening day,
  // not the normal way in. Only sources with something outstanding are shown.
  const outstanding = useMemo(() => outstandingNotes(cards, notes, lang), [cards, notes, lang]);

  // Sources are tags, not decks: this list is for cramming on listening day,
  // not the normal way in. Counted per note so it agrees with the queue.
  const recentSources = useMemo(() => {
    const perSource = new Map<string, number>();
    for (const note of notes) {
      if (!note.source_id || !outstanding.has(note.id)) continue;
      perSource.set(note.source_id, (perSource.get(note.source_id) ?? 0) + 1);
    }
    return sources
      .filter((s) => s.lang === lang && s.kind !== "capture" && perSource.has(s.id))
      .sort((a, b) => b.created_at - a.created_at)
      .slice(0, 8)
      .map((s) => ({ source: s, count: perSource.get(s.id) ?? 0 }));
  }, [notes, sources, lang, outstanding]);

  const miningDue = useMemo(
    () =>
      notes.filter((n) => n.lang === lang && n.domain === "mining" && outstanding.has(n.id)).length,
    [notes, lang, outstanding],
  );

  const pendingNotes = notes.filter((n) => n.lang === lang && n.status === "pending").length;
  const nothingYet = summary.total === 0 && pendingNotes === 0;

  return (
    <>
      <h1>{LANG_NAMES[lang]}</h1>
      <p className="lede">
        {nothingYet
          ? "No cards yet. Add a podcast transcript or a document to get started."
          : `${counts.reviewed} reviewed today · ${newRemaining} new still allowed`}
      </p>

      <div className="counts">
        <div className="card count due">
          <span className="n">{summary.due}</span>
          <span className="k">Due</span>
        </div>
        <div className="card count">
          <span className="n">{availableNew}</span>
          <span className="k">New</span>
        </div>
        <div className="card count">
          <span className="n">{summary.total}</span>
          <span className="k">Cards</span>
        </div>
      </div>

      <div style={{ marginTop: 14 }}>
        <button
          className="btn primary full"
          disabled={summary.due + availableNew === 0}
          onClick={() => onReview(null, null)}
        >
          {summary.due + availableNew === 0
            ? "Nothing due"
            : `Review ${summary.due + availableNew} card${summary.due + availableNew === 1 ? "" : "s"}`}
        </button>
      </div>

      {pendingNotes > 0 && (
        <>
          <h2>Waiting for you</h2>
          <button className="sourceitem" onClick={onGoToInbox}>
            <div className="meta">
              <div className="title">{pendingNotes} item{pendingNotes === 1 ? "" : "s"} to approve</div>
              <div className="sub">Captured words and saved extractions</div>
            </div>
            <span className="n">›</span>
          </button>
        </>
      )}

      {recentSources.length > 0 && (
        <>
          <h2>Cram one episode</h2>
          <p className="lede" style={{ marginTop: -4 }}>
            For the day you listen. Everything here is also in the main queue.
          </p>
          <div className="sourcelist">
            {recentSources.map(({ source, count }) => (
              <button key={source.id} className="sourceitem" onClick={() => onReview(source.id, null)}>
                <div className="meta">
                  <div className="title">{source.title}</div>
                  <div className="sub">
                    {source.episode_date ?? new Date(source.created_at).toLocaleDateString()}
                    {source.kind === "document" ? " · document" : ""}
                  </div>
                </div>
                <span className="n">{count}</span>
              </button>
            ))}
          </div>
        </>
      )}

      {miningDue > 0 && (
        <>
          <h2>Before a meeting</h2>
          <button className="sourceitem" onClick={() => onReview(null, "mining")}>
            <div className="meta">
              <div className="title">Mining terminology</div>
              <div className="sub">Domain-tagged cards only</div>
            </div>
            <span className="n">{miningDue}</span>
          </button>
        </>
      )}
    </>
  );
}
